import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { PREDEFINED_VENUES } from '../constants/campusVenues';
import { IssuePassModal } from './Passes';

const EVENT_TYPES = ['meetup', 'seminar', 'workshop', 'conference', 'guest_lecture', 'interview', 'other'];

const emptyForm = () => ({
  title: '',
  date: '',
  venue: '',
  type: 'seminar',
  maxGuests: 50,
});

function isApproved(event) {
  return event?.approvalStatus === 'approved';
}

function EventModal({ event, onClose, onSave, onIssuePass }) {
  const [form, setForm] = useState(emptyForm);
  const [venueSelect, setVenueSelect] = useState('');
  const [venueOther, setVenueOther] = useState('');
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      const v = event.venue || '';
      if (PREDEFINED_VENUES.includes(v)) {
        setVenueSelect(v);
        setVenueOther('');
      } else {
        setVenueSelect('__other__');
        setVenueOther(v);
      }
      setForm({
        title: event.title || '',
        date: event.date ? String(event.date).substring(0, 10) : '',
        venue: event.venue || '',
        type: event.type || 'seminar',
        maxGuests: event.maxGuests ?? 50,
      });
    } else {
      setVenueSelect('');
      setVenueOther('');
      setForm(emptyForm());
    }
  }, [event]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const resolvedVenue = venueSelect === '__other__' ? venueOther.trim() : venueSelect;

  const conflictingBookings = useMemo(() => {
    const list = bookings || [];
    if (!event?._id) return list;
    return list.filter((b) => String(b._id) !== String(event._id));
  }, [bookings, event]);

  useEffect(() => {
    const d = form.date ? String(form.date).substring(0, 10) : '';
    if (!resolvedVenue || !d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      setBookings([]);
      return;
    }

    let cancel = false;
    setBookingsLoading(true);
    api
      .get('/venues/bookings', { params: { venue: resolvedVenue, date: d } })
      .then((r) => {
        if (!cancel) setBookings(r.data.bookings || []);
      })
      .catch(() => {
        if (!cancel) setBookings([]);
      })
      .finally(() => {
        if (!cancel) setBookingsLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [resolvedVenue, form.date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!venueSelect) {
      toast.error('Select a venue');
      return;
    }
    if (venueSelect === '__other__' && !venueOther.trim()) {
      toast.error('Enter the other venue name');
      return;
    }
    const payload = { ...form, venue: resolvedVenue };

    setLoading(true);
    try {
      if (event?._id) {
        await api.put(`/events/${event._id}`, payload);
        toast.success('Event updated!');
      } else {
        await api.post('/events', payload);
        toast.success('Event created!');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={(ev) => ev.target === ev.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{event?._id ? 'Edit event' : 'Create event'}</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label htmlFor="ev-title">Event title *</label>
              <input
                id="ev-title"
                required
                placeholder="e.g., Industry expert talk on AI"
                value={form.title}
                onChange={(ev) => set('title', ev.target.value)}
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="ev-date">Date *</label>
                <input
                  id="ev-date"
                  type="date"
                  required
                  min={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10)}
                  value={form.date ? form.date.substring(0, 10) : ''}
                  onChange={(ev) => set('date', ev.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="ev-type">Event type *</label>
                <select id="ev-type" value={form.type} onChange={(ev) => set('type', ev.target.value)}>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="ev-venue-preset">Venue *</label>
                <select
                  id="ev-venue-preset"
                  required
                  value={venueSelect}
                  onChange={(ev) => setVenueSelect(ev.target.value)}
                >
                  <option value="">Select venue…</option>
                  {PREDEFINED_VENUES.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                  <option value="__other__">Other — type room / hall name</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="ev-cap">Max guests</label>
                <input id="ev-cap" type="number" min="1" max="500" value={form.maxGuests} onChange={(ev) => set('maxGuests', +ev.target.value)} />
              </div>
            </div>
            {venueSelect === '__other__' && (
              <div className="field">
                <label htmlFor="ev-venue-other">Other venue name *</label>
                <input
                  id="ev-venue-other"
                  required
                  placeholder="e.g., Lab B204, Admin block seminar room"
                  value={venueOther}
                  onChange={(ev) => setVenueOther(ev.target.value)}
                />
              </div>
            )}

            {resolvedVenue && form.date && (
              <div
                className="section-card"
                style={{
                  padding: '0.875rem 1rem',
                  background: conflictingBookings.length ? 'rgba(251,191,36,0.12)' : 'var(--gray-50)',
                  borderColor: conflictingBookings.length ? 'rgba(251,191,36,0.65)' : 'var(--border-1)',
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-2)' }}>
                  Venue schedule on this date
                </div>
                {bookingsLoading && <p style={{ fontSize: 'var(--text-sm)', marginTop: 8, color: 'var(--text-3)' }}>Checking calendar…</p>}
                {!bookingsLoading && conflictingBookings.length === 0 && (
                  <p style={{ fontSize: 'var(--text-sm)', marginTop: 8, color: 'var(--text-3)' }}>No other events use this venue on this date.</p>
                )}
                {!bookingsLoading && conflictingBookings.length > 0 && (
                  <ul style={{ marginTop: 10, paddingLeft: 18, fontSize: 'var(--text-sm)', color: 'var(--text-2)', lineHeight: 1.5 }}>
                    {conflictingBookings.map((b) => (
                      <li key={b._id}>
                        <strong>{b.title}</strong> — hosted by {b.faculty?.name || 'Faculty'} · up to {b.maxGuests} guests · {b.type?.replace('_', ' ') || ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            {event?._id && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => onIssuePass?.(event)}
                disabled={!isApproved(event)}
                style={{ opacity: isApproved(event) ? 1 : 0.5 }}
                title={isApproved(event) ? 'Issue passes' : 'Awaiting AO approval'}
              >
                Issue passes
              </button>
            )}
            <button 
              type="submit" 
              className="btn btn-navy" 
              disabled={loading || conflictingBookings.length > 0}
              style={{ opacity: conflictingBookings.length > 0 ? 0.5 : 1 }}
            >
              {loading ? (
                <>
                  <span className="spinner" /> Saving…
                </>
              ) : event?._id ? (
                'Update event'
              ) : (
                'Create event'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [showIssuePass, setShowIssuePass] = useState(false);
  const [issueEvent, setIssueEvent] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('/events')
      .then((r) => setEvents(r.data.events || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleEdit = (e) => {
    setEditEvent(e);
    setShowModal(true);
  };
  const handleNew = () => {
    setEditEvent(null);
    setShowModal(true);
  };
  const handleSave = () => {
    setShowModal(false);
    load();
  };

  const handleIssuePass = (event) => {
    setShowModal(false);
    setIssueEvent(event);
    setShowIssuePass(true);
  };

  const handleIssueIssued = () => {
    setShowIssuePass(false);
    setIssueEvent(null);
    load();
  };

  const typeColor = {
    meetup: '#7c3aed',
    seminar: '#0f766e',
    workshop: '#059669',
    conference: '#d97706',
    guest_lecture: '#be185d',
    interview: '#0891b2',
    other: '#64748b',
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">My events</div>
          <div className="page-subtitle">
            {events.length} event{events.length !== 1 ? 's' : ''} created
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-navy" onClick={handleNew}>
            + Create event
          </button>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-loading">
            <div className="spinner" />
          </div>
        ) : events.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-icon">Start here</div>
              <div className="empty-title">No events yet</div>
              <div className="empty-text">Create an event with a venue and date, then issue passes.</div>
              <button type="button" className="btn btn-navy" style={{ margin: '1rem auto 0', display: 'flex' }} onClick={handleNew}>
                + Create event
              </button>
            </div>
          </div>
        ) : (
          <div className="events-grid">
            {events.map((ev) => {
              const strip = typeColor[ev.type] || '#64748b';
              const fillPct = Math.min(100, ((ev.passCount || 0) / ev.maxGuests) * 100);
              const faded = !!ev.dateConflict;
              return (
                <div
                  key={ev._id}
                  className="card fade-in event-tile"
                  style={{ '--event-strip': strip, opacity: faded ? 0.55 : 1, filter: faded ? 'grayscale(0.2)' : 'none' }}
                >
                  <div className="event-title-row">
                    <span className={`badge badge-${ev.status}`}>{ev.status}</span>
                    <span
                      className="badge"
                      style={{ background: ev.approvalStatus === 'approved' ? 'rgba(16,185,129,0.12)' : 'rgba(217,119,6,0.12)', color: ev.approvalStatus === 'approved' ? '#047857' : '#b45309' }}
                    >
                      {ev.approvalStatus === 'approved' ? 'approved' : ev.approvalStatus || 'pending'}
                    </span>
                    <span
                      className="event-type-pill"
                      style={{
                        color: strip,
                        background: `${strip}14`,
                        border: `1px solid ${strip}44`,
                      }}
                    >
                      {ev.type?.replace('_', ' ')}
                    </span>
                    {faded && (
                      <span className="badge" style={{ background: 'rgba(124,45,18,0.08)', color: '#92400e', border: '1px solid rgba(124,45,18,0.18)' }}>
                        date conflict
                      </span>
                    )}
                  </div>
                  <h3 className="event-title">{ev.title}</h3>
                  <div className="event-meta-stack">
                    <div className="event-meta-line">{new Date(ev.date).toLocaleDateString('en-IN', { dateStyle: 'full' })}</div>
                    <div className="event-meta-line">{ev.venue}</div>
                  </div>
                  <div className="event-capacity-bar">
                    <div>
                      <div className="event-cap-num">{ev.passCount || 0}</div>
                      <div className="event-cap-muted">Passes issued</div>
                    </div>
                    <div className="event-capacity-middle">
                      <div className="event-cap-num-muted">{ev.maxGuests}</div>
                      <div className="event-cap-muted">Capacity</div>
                    </div>
                    <div className="event-progress-track">
                      <div className="event-progress-fill" style={{ width: `${fillPct}%` }} />
                    </div>
                  </div>
                  <div className="event-card-actions">
                    <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => handleEdit(ev)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-navy btn-sm"
                      style={{ flex: 1, opacity: isApproved(ev) ? 1 : 0.5 }}
                      onClick={() => handleIssuePass(ev)}
                      disabled={!isApproved(ev)}
                      title={isApproved(ev) ? 'Issue passes' : 'Awaiting AO approval'}
                    >
                      Issue passes
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && <EventModal event={editEvent} onClose={() => setShowModal(false)} onSave={handleSave} onIssuePass={handleIssuePass} />}
      {showIssuePass && issueEvent && (
        <IssuePassModal
          events={events}
          initialEventId={issueEvent._id}
          onClose={() => {
            setShowIssuePass(false);
            setIssueEvent(null);
          }}
          onIssued={handleIssueIssued}
        />
      )}
    </div>
  );
}
